import React from 'react';
import { type MessageDescriptor } from 'react-intl';
import { getHeapPageArea } from '@eventbrite/legacy-eds.utils/tracking';
import { EmailShare } from './EmailShare';
import { FacebookShare } from './FacebookShare';
import { FacebookMessengerShare } from './FacebookMessengerShare';
import { LinkedinShare } from './LinkedinShare';
import { TwitterShare } from './TwitterShare';
import { WhatsappShare } from './WhatsappShare';
import {
    SHARE_ON_EMAIL,
    SHARE_ON_FACEBOOK,
    SHARE_ON_FACEBOOK_MESSENGER,
    SHARE_ON_LINKEDIN,
    SHARE_ON_TWITTER,
    SHARE_ON_WHATSAPP,
    DEFAULT_SHARE_TYPES,
    SHARE_TYPES,
    DEFAULT_UTM_OPTIONS,
    UTM_SHARE_TYPE_MAP,
    DATA_SHARE_METHOD_MAP,
} from './constants';
import type { UtmOptions } from './types';
import styles from './ShareBox.module.scss';

interface ShareBoxProps {
    resourceId: string;
    resourceName: string;
    resourceUrl: string;
    resourceType: string;
    facebookOptions: {
        appId: string;
        locale: string;
        page: string;
        version: string;
        xfbml: boolean;
    };
    serverUrl: string;
    shareTypes?: typeof SHARE_TYPES;
    twitterHandle: string;
    isMobile?: boolean;
    onClick?: (shareType: string) => void;
    utmOptions?: UtmOptions;
    emailOptions?: {
        bodyCopy?: MessageDescriptor;
        subjectCopy?: MessageDescriptor;
    };
    affCode?: string;
}

interface ShareOptions extends Omit<ShareBoxProps, 'shareTypes' | 'utmOptions'> {
    staticUrl?: boolean;
}

const SHARE_TYPE_COMPONENTS_MAP = {
    [SHARE_ON_EMAIL]: EmailShare,
    [SHARE_ON_FACEBOOK]: FacebookShare,
    [SHARE_ON_FACEBOOK_MESSENGER]: FacebookMessengerShare,
    [SHARE_ON_TWITTER]: TwitterShare,
    [SHARE_ON_LINKEDIN]: LinkedinShare,
    [SHARE_ON_WHATSAPP]: WhatsappShare,
};

const _getShareComponent = (
    mergedUtmOptions: UtmOptions,
    options: ShareOptions,
    shareType: string,
    dataPageArea: string,
    resourceType: string,
) => {
    let component: React.ReactElement | null = null;

    if (SHARE_TYPES.includes(shareType)) {
        let boundOnClick;
        const ShareComponent = SHARE_TYPE_COMPONENTS_MAP[shareType];
        const shareMethod = DATA_SHARE_METHOD_MAP[shareType];

        const { onClick, ...shareOptions } = options;

        const typeSpecificUtmOptions = {
            ...mergedUtmOptions,
            ...UTM_SHARE_TYPE_MAP[shareType],
        };

        if (onClick) {
            boundOnClick = onClick.bind(null, shareType);
        }

        if (shareType === SHARE_ON_LINKEDIN) {
            shareOptions.staticUrl = true;
        }

        component = (
            <ShareComponent
                className={styles.shareIcon}
                key={shareType}
                onClick={boundOnClick}
                utmOptions={typeSpecificUtmOptions}
                dataHeapId={`share-${resourceType}-method`}
                shareMethod={shareMethod}
                dataPageArea={dataPageArea}
                {...shareOptions}
            />
        );
    }

    return component;
};

const _generateShareComponents = (
    shareTypes: typeof SHARE_TYPES,
    mergedUtmOptions: UtmOptions,
    options: ShareOptions,
    dataPageArea: string,
    resourceType: string,
) => {
    const types = shareTypes || DEFAULT_SHARE_TYPES;
    return types
        .map((shareType) =>
            _getShareComponent(
                mergedUtmOptions,
                options,
                shareType as string,
                dataPageArea,
                resourceType,
            ),
        )
        .filter(Boolean);
};

export class ShareBox extends React.Component<ShareBoxProps> {
    state = {
        dataPageArea: getHeapPageArea(this.props.affCode) || '',
    };

    componentDidMount() {
        this.setState({ dataPageArea: getHeapPageArea(this.props.affCode) || '' });
    }

    render() {
        const { shareTypes, utmOptions, ...options } = this.props;

        const mergedUtmOptions = {
            ...DEFAULT_UTM_OPTIONS,
            ...utmOptions,
        };

        const shareComponents = _generateShareComponents(
            shareTypes || DEFAULT_SHARE_TYPES,
            mergedUtmOptions,
            options,
            this.state.dataPageArea,
            this.props.resourceType,
        );

        return <div data-testid="share-box-container">{shareComponents}</div>;
    }
}
